import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Globe, Package, Plus, Loader2, Building2 } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/fornecedor-internacional`;

/**
 * Painel do fornecedor internacional - cadeia de suprimento separada da
 * brasileira, atende exclusivamente afiliados com plano internacional.
 */
const FornecedorInternacional = () => {
  const usuarioId = authService.getUser()?.id || "";

  const [carregando, setCarregando] = useState(true);
  const [fornecedor, setFornecedor] = useState(null);
  const [produtos, setProdutos] = useState([]);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [novoProduto, setNovoProduto] = useState({
    nome: "", categoria: "", preco_usd: "", custo_fornecedor_usd: "", estoque: "", sku: ""
  });

  const carregar = async () => {
    setCarregando(true);
    try {
      const [respForn, respProd] = await Promise.all([
        authService.fetchAuth(`${API}/${usuarioId}`),
        authService.fetchAuth(`${API}/${usuarioId}/produtos`),
      ]);
      if (respForn.ok) setFornecedor(await respForn.json());
      if (respProd.ok) setProdutos(await respProd.json());
    } catch (error) {
      console.error("Erro ao carregar fornecedor internacional:", error);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const salvarProduto = async () => {
    if (!novoProduto.nome || !novoProduto.preco_usd) {
      toast.error("Preencha ao menos nome e preço.");
      return;
    }
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/${usuarioId}/produto`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          nome: novoProduto.nome,
          categoria: novoProduto.categoria,
          preco_usd: parseFloat(novoProduto.preco_usd),
          custo_fornecedor_usd: parseFloat(novoProduto.custo_fornecedor_usd || 0),
          estoque: parseInt(novoProduto.estoque || 0),
          sku: novoProduto.sku,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao cadastrar produto");
      toast.success("Produto internacional cadastrado!");
      setMostrarForm(false);
      setNovoProduto({ nome: "", categoria: "", preco_usd: "", custo_fornecedor_usd: "", estoque: "", sku: "" });
      await carregar();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-96 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Carregando...
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Building2 className="w-7 h-7 text-blue-400" />
        <div>
          <h1 className="text-2xl font-bold text-white">Fornecedor Internacional</h1>
          <p className="text-sm text-gray-400">
            {fornecedor ? `${fornecedor.nome_empresa} · ${fornecedor.pais}` : "Cadeia de suprimento internacional"}
          </p>
        </div>
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-white">Meus Produtos ({produtos.length})</h2>
        <Button onClick={() => setMostrarForm(!mostrarForm)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" />
          Novo Produto
        </Button>
      </div>

      {mostrarForm && (
        <Card className="p-4 bg-gray-800/50 border-gray-700 mb-4 space-y-3">
          <Input
            placeholder="Nome do produto"
            value={novoProduto.nome}
            onChange={(e) => setNovoProduto({ ...novoProduto, nome: e.target.value })}
          />
          <Input
            placeholder="Categoria"
            value={novoProduto.categoria}
            onChange={(e) => setNovoProduto({ ...novoProduto, categoria: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-3">
            <Input
              placeholder="Preço venda (USD)"
              type="number"
              value={novoProduto.preco_usd}
              onChange={(e) => setNovoProduto({ ...novoProduto, preco_usd: e.target.value })}
            />
            <Input
              placeholder="Custo (USD)"
              type="number"
              value={novoProduto.custo_fornecedor_usd}
              onChange={(e) => setNovoProduto({ ...novoProduto, custo_fornecedor_usd: e.target.value })}
            />
            <Input
              placeholder="Estoque"
              type="number"
              value={novoProduto.estoque}
              onChange={(e) => setNovoProduto({ ...novoProduto, estoque: e.target.value })}
            />
          </div>
          <Input
            placeholder="SKU"
            value={novoProduto.sku}
            onChange={(e) => setNovoProduto({ ...novoProduto, sku: e.target.value })}
          />
          <Button onClick={salvarProduto} disabled={salvando} className="bg-green-600 hover:bg-green-700 w-full">
            {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Salvar Produto
          </Button>
        </Card>
      )}

      <div className="space-y-2">
        {produtos.length === 0 && (
          <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
            Nenhum produto internacional cadastrado ainda.
          </Card>
        )}
        {produtos.map((p) => (
          <Card key={p.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Package className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-white font-medium">{p.nome}</p>
                <p className="text-xs text-gray-500">{p.categoria} · Estoque: {p.estoque}</p>
              </div>
            </div>
            <p className="text-green-400 font-bold">${(p.preco_usd || 0).toFixed(2)}</p>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default FornecedorInternacional;
